import type * as React from 'react';
import './i18next';
import { Routes, Route } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import {
  BeltechEduPage,
  ContactPage,
  PersonDetailsPage,
  MagazinePage,
  Header,
  Footer,
  MediaPage,
  SimplePage,
  ArticlePage,
  VenuePage,
  HomePage,
  NotFoundPage,
  SpeakersPage,
  ScrollToTop,
  SchedulePage,
  PanelDetailsPage,
  type Person,
  type ScheduleData,
  type SpeakerReference,
  type SpeakerRole,
  type TranslationFunction,
} from '@kainossoftwareltd/convention-frontend-components';
import curators from './assets/2026/static-data/curators.json';
import speakers from './assets/2026/static-data/speakers.json';
import eduSpeakers from './assets/2026/static-data/eduSpeakers.json';
import schedule from './assets/2026/static-data/schedule.json';
import articles from './assets/2026/static-data/articles.json';
import headerInfo from './assets/2025/static-data/header.json';
import footerInfo from './assets/2025/static-data/footer.json';
import contactDetails from './assets/2025/static-data/contact.json';
import magazines from './assets/2025/static-data/magazines.json';
import galleries from './assets/2025/static-data/galleries.json';
import beltechEdu from './assets/2026/static-data/beltechEdu.json';

type RouteWrapperProps = {
  element?: React.ReactElement;
  comingSoon?: boolean;
  t: TranslationFunction;
};

type PanelScheduleData = NonNullable<React.ComponentProps<typeof PanelDetailsPage>['scheduleData']>;
type PanelEvent = NonNullable<PanelScheduleData['schedules']>[number]['events'][number];
type SpeakerData = NonNullable<NonNullable<React.ComponentProps<typeof PanelDetailsPage>['speakersData']>[number]>;
type SchedulePageData = React.ComponentProps<typeof SchedulePage>['scheduleData'];

const isRecord = (value: unknown): value is Record<string, unknown> =>
  typeof value === 'object' && value !== null;

const toSpeakerRole = (value: unknown): SpeakerRole | undefined => {
  if (value === 'speaker' || value === 'curator' || value === 'other') {
    return value;
  }
  return undefined;
};

const toSpeakerReferences = (value: unknown): SpeakerReference[] | undefined => {
  if (!Array.isArray(value)) {
    return undefined;
  }

  const references = value
    .map((item): SpeakerReference | undefined => {
      if (!isRecord(item)) {
        return undefined;
      }

      const speakerId = item.speakerId;
      if (typeof speakerId !== 'string' && typeof speakerId !== 'number') {
        return undefined;
      }

      const chair =
        typeof item.chair === 'boolean'
          ? item.chair
          : item.chair === 'true'
            ? true
            : item.chair === 'false'
              ? false
              : undefined;

      return {
        speakerId,
        role: toSpeakerRole(item.role),
        chair,
      };
    })
    .filter((reference): reference is SpeakerReference => reference !== undefined);

  return references.length > 0 ? references : undefined;
};

const normaliseScheduleData = (value: unknown): ScheduleData | undefined => {
  if (!isRecord(value) || !Array.isArray(value.schedules)) {
    return undefined;
  }

  const schedules = value.schedules
    .map((day): NonNullable<ScheduleData['schedules']>[number] | undefined => {
      if (!isRecord(day) || typeof day.day !== 'number' || !Array.isArray(day.events)) {
        return undefined;
      }

      const events = day.events
        .map((event): NonNullable<NonNullable<ScheduleData['schedules']>[number]['events'][number]> | undefined => {
          if (!isRecord(event)) {
            return undefined;
          }

          return {
            id: typeof event.id === 'string' || typeof event.id === 'number' ? event.id : undefined,
            time: typeof event.time === 'string' ? event.time : undefined,
            title: typeof event.title === 'string' ? event.title : undefined,
            subtitle: typeof event.subtitle === 'string' ? event.subtitle : undefined,
            abstract: typeof event.abstract === 'string' ? event.abstract : undefined,
            link: typeof event.link === 'boolean' ? event.link : undefined,
            panel: typeof event.panel === 'boolean' ? event.panel : undefined,
            panelId: typeof event.panelId === 'string' || typeof event.panelId === 'number' ? event.panelId : undefined,
            debate: typeof event.debate === 'boolean' ? event.debate : undefined,
            debateId: typeof event.debateId === 'string' || typeof event.debateId === 'number' ? event.debateId : undefined,
            speakers: toSpeakerReferences(event.speakers),
          };
        })
        .filter((eventItem): eventItem is NonNullable<NonNullable<ScheduleData['schedules']>[number]['events'][number]> => eventItem !== undefined);

      return {
        day: day.day,
        date: typeof day.date === 'string' ? day.date : undefined,
        events,
      };
    })
    .filter((scheduleDay): scheduleDay is NonNullable<ScheduleData['schedules']>[number] => scheduleDay !== undefined);

  return { schedules };
};

const toSpeakerDataLookup = (people: Person[]): Map<string, SpeakerData> => {
  const map = new Map<string, SpeakerData>();

  for (const person of people) {
    if (person.speakerId === undefined) {
      continue;
    }

    const speaker: SpeakerData = {
      speakerId: person.speakerId,
      id: person.id,
      name: person.name,
      jobTitle: person.jobTitle,
      company: person.company,
      image: person.image,
      bio: person.bio,
      description: person.description,
      teamName: person.teamName,
      teamLabel: person.teamLabel,
      team: person.team,
      teamId: person.teamId,
      side: person.side,
    };

    map.set(String(person.speakerId), speaker);
  }

  return map;
};

const toPanelSpeakers = (
  speakers: SpeakerReference[] | undefined,
  lookup: Map<string, SpeakerData>
): SpeakerData[] | undefined => {
  if (!speakers) {
    return undefined;
  }

  const mapped = speakers
    .map((speakerRef): SpeakerData | undefined => {
      if (speakerRef.speakerId === undefined) {
        return undefined;
      }

      const speaker = lookup.get(String(speakerRef.speakerId));
      if (!speaker) {
        return undefined;
      }

      return {
        ...speaker,
        role: speakerRef.role,
        chair: speakerRef.chair,
      };
    })
    .filter((speaker): speaker is SpeakerData => speaker !== undefined);

  return mapped.length > 0 ? mapped : undefined;
};

const normalisePanelScheduleData = (
  value: unknown,
  lookup: Map<string, SpeakerData>
): PanelScheduleData | undefined => {
  const scheduleData = normaliseScheduleData(value);
  if (!scheduleData?.schedules) {
    return undefined;
  }

  return {
    schedules: scheduleData.schedules.map((scheduleDay) => ({
      day: scheduleDay.day,
      date: scheduleDay.date,
      events: scheduleDay.events.map((event): PanelEvent => ({
        panel: event.panel,
        panelId: event.panelId,
        title: event.title,
        time: event.time,
        abstract: event.abstract,
        speakers: toPanelSpeakers(event.speakers, lookup),
      })),
    })),
  };
};

const normaliseSchedulePageData = (scheduleData: ScheduleData | undefined): SchedulePageData => {
  if (!scheduleData?.schedules) {
    return undefined;
  }

  return {
    schedules: scheduleData.schedules.map((day) => ({
      day: day.day,
      date: day.date || '',
      events: day.events,
    })),
    trackInfo: scheduleData.trackInfo,
  };
};

const RouteWrapper = ({ element, comingSoon, t }: RouteWrapperProps): React.ReactElement => {
  if (comingSoon || !element) {
    return <SimplePage page="ComingSoon" t={t} />;
  }
  return element;
};

function App() {
  const { t } = useTranslation();
  const newsletterApiUrl = process.env.REACT_APP_BELTECH_NEWSLETTER_API_URL;
  const apiUrl = process.env.REACT_APP_BELTECH_EMAIL_API_URL;
  const recaptchaKey = process.env.REACT_APP_BELTECH_RECAPTCHA_KEY;
  const soldOut = process.env.REACT_APP_SOLD_OUT === 'true';
  const ticketsSoon = process.env.REACT_APP_TICKETS_SOON === 'true';
  // The call for speakers section stays visible while a submission link is set.
  // To close the call, clear "Pages.Home.CallForSpeakersLink" in the translation file.
  const hasCallForSpeakersLink = Boolean(
    t('Pages.Home.CallForSpeakersLink', { defaultValue: '' })
  );
  const callForSpeakersExpirationDate = hasCallForSpeakersLink
    ? '2999-12-31'
    : undefined;
  const earlyBird = process.env.REACT_APP_EARLY_BIRD === 'true';
  const earlyBirdLink = process.env.REACT_APP_EARLY_BIRD_LINK || '';
  const DayTwoTicketsButton = process.env.REACT_APP_SHOW_DAY_TWO_TICKETS_BUTTON === 'true';
  const allSpeakers = [...speakers, ...eduSpeakers];
  const scheduleData = normaliseScheduleData(schedule);
  const schedulePageData = normaliseSchedulePageData(scheduleData);
  const speakerLookup = toSpeakerDataLookup([...allSpeakers, ...curators]);
  const panelScheduleData = normalisePanelScheduleData(schedule, speakerLookup);

  const routesConfig = [
    { path: '/*', element: <NotFoundPage t={t} /> },
    {
      path: '/',
      element: (
        <HomePage
          t={t}
          soldOut={soldOut}
          ticketsSoon={ticketsSoon}
          curatorsData={curators}
          callForSpeakersExpirationDate={callForSpeakersExpirationDate}
          earlyBird={earlyBird}
          earlyBirdLink={earlyBirdLink}
          showDayTwoTicketsButton={DayTwoTicketsButton}
          showTopics={false}
        />
      ),
    },
    { path: '/venue', element: <VenuePage t={t} /> },
    {
      path: '/speakers',
      element: <SimplePage t={t} page="Speakers" />,
    },
    {
      path: '/speakers/:speaker/:speakerId',
      element: (
        <PersonDetailsPage
          t={t}
          scheduleData={scheduleData}
          speakersList={allSpeakers}
          page="speakers"
        />
      ),
    },
    {
      path: '/curators/:curator/:speakerId',
      element: (
        <PersonDetailsPage
          t={t}
          scheduleData={scheduleData}
          speakersList={curators}
          page="curators"
        />
      ),
    },
    {
      path: '/schedule',
      element: <SimplePage t={t} page="BelTechSchedule" />,
    },
    {
      path: '/contact',
      element: (
        <ContactPage
          t={t}
          contactDetails={contactDetails}
          apiUrl={apiUrl}
          recaptchaKey={recaptchaKey}
        />
      ),
    },
    { path: '/beltech-edu',
      element: (
        <BeltechEduPage 
          t={t} 
          sponsors={beltechEdu.sponsors}
          heroImages={beltechEdu.heroImages}
          heroImageAlt={beltechEdu.heroImageAlt}
          headerInfo={headerInfo}
        />
      ), },
    {
      path: '/magazines/:magazineId',
      element: <MagazinePage t={t} magazines={magazines} />,
    },
    { path: '/coming-soon', comingSoon: true },
    {
      path: '/media',
      element: <MediaPage mediaData={{ galleries, articles, magazines }} t={t} mediaDescriptions={{
        articles: 'Check out our articles featuring the latest news from Beltech.',
        magazines: 'Our magazines feature themes, and speakers from our events, as well as the advancements in the tech industry and opportunities for talent development.',
        galleries: 'Browse our photo galleries from previous BelTech events.'
      }} />,
    },
    {
      path: '/articles/:articleId',
      element: <ArticlePage t={t} articles={articles} />,
    },
    {
      path: '/panel/:panelId',
      element: (
        <PanelDetailsPage t={t}
        scheduleData={panelScheduleData}
        speakersData={allSpeakers}
        curatorsData={curators} />
      ),
      comingSoon: false,
    },

  ];

  return (
    <div className="con-app con-text zoomout-limited">
      <Header headerInfo={headerInfo} t={t} soldOut={soldOut} ticketsSoon={ticketsSoon} />
      <ScrollToTop />
      <main className="con-app__container">
        <Routes>
          {routesConfig.map(({ path, element, comingSoon }) => (
            <Route
              key={path}
              path={path}
              element={
                <RouteWrapper element={element} comingSoon={comingSoon} t={t} />
              }
            />
          ))}
        </Routes>
      </main>
      <Footer footerInfo={footerInfo} apiUrl={newsletterApiUrl || ''} />
    </div>
  );
}

export default App;